/**
 * Em'z Blendz Skin Quiz
 * State machine + scoring engine + product fetcher
 *
 * Question order: Skin Type → Category → Concerns → Scent
 * Scent weight is zeroed when "face", "mens", "baby", or "lip"
 * category is selected alone.
 * Scent is multi-select: +4 if any overlap, -3 if zero overlap.
 * Results: 6 products, distributed across selected categories.
 * Match reasons are generated dynamically from quiz answers.
 */
(function() {
  'use strict';

  const SCREENS = ['welcome', 'q1', 'q2', 'q3', 'q4', 'loading', 'results'];

  /**
   * Map of data-quiz-cards key → next screen.
   * Q1 skinType (single) → Q2
   * Q2 category (multi)  → Q3
   * Q3 concerns (multi)  → Q4
   * Q4 scent (multi)     → loading
   */
  const ADVANCE_MAP = {
    skinType: 'q2',
    category: 'q3',
    concerns: 'q4',
    scent:    'loading'
  };

  /**
   * Map screen id → answer key for multi-select "Next" buttons.
   */
  const SCREEN_KEY_MAP = {
    q2: 'category',
    q3: 'concerns',
    q4: 'scent'
  };

  const quiz = {
    el: null,
    currentScreen: 'welcome',
    answers: { skinType: null, concerns: [], scent: [], category: [] },
    proxyBtn: null,

    init() {
      this.el = document.querySelector('[data-quiz]');
      if (!this.el) return;
      this.bindEvents();
      this.updateProgress();

      /* Mobile: body-level proxy Continue button + widget hiding */
      if (window.innerWidth < 768) {
        this.createMobileProxy();
        this.hideFloatingWidgets();
      }
    },

    bindEvents() {
      /* Start button */
      this.el.querySelector('[data-quiz-start]')
        .addEventListener('click', () => this.goTo('q1'));

      /* Back button */
      this.el.querySelector('[data-quiz-back]')
        .addEventListener('click', () => this.goBack());

      /* Retake */
      this.el.querySelector('[data-quiz-retake]')
        .addEventListener('click', () => this.retake());

      /* Card clicks */
      this.el.querySelectorAll('[data-quiz-cards]').forEach(group => {
        const key = group.dataset.quizCards;
        const mode = group.dataset.select; /* single | multi */

        group.querySelectorAll('.emz-quiz__card').forEach(card => {
          card.addEventListener('click', () => {
            const val = card.dataset.value;

            if (mode === 'single') {
              /* Deselect siblings */
              group.querySelectorAll('.emz-quiz__card').forEach(c =>
                c.classList.remove('emz-quiz__card--selected'));
              card.classList.add('emz-quiz__card--selected');
              this.answers[key] = val;

              /* Auto-advance after short delay */
              setTimeout(() => this.autoAdvance(key), 350);
            } else {
              /* Multi-select: toggle */
              /* "none" and "any" are exclusive — selecting one
                 deselects everything else.  Selecting a regular
                 option deselects any active exclusive card. */
              const exclusiveValues = ['none', 'any'];

              if (exclusiveValues.includes(val)) {
                group.querySelectorAll('.emz-quiz__card').forEach(c => {
                  if (c.dataset.value !== val) c.classList.remove('emz-quiz__card--selected');
                });
                card.classList.toggle('emz-quiz__card--selected');
              } else {
                /* Deselect any exclusive card when a specific option is picked */
                group.querySelectorAll('.emz-quiz__card').forEach(c => {
                  if (exclusiveValues.includes(c.dataset.value)) c.classList.remove('emz-quiz__card--selected');
                });
                card.classList.toggle('emz-quiz__card--selected');
              }

              /* Collect selected values */
              this.answers[key] = Array.from(
                group.querySelectorAll('.emz-quiz__card--selected')
              ).map(c => c.dataset.value);
            }

            /* Show/hide the Continue button for multi-select */
            const screen = group.closest('[data-quiz-screen]');
            const nextBtn = screen.querySelector('[data-quiz-next]');
            if (nextBtn && mode === 'multi') {
              const hasSelection = group.querySelectorAll('.emz-quiz__card--selected').length > 0;
              nextBtn.classList.toggle('emz-quiz__next--visible', hasSelection);
              this.syncProxy();
            }
          });
        });
      });

      /* Next buttons (for multi-select screens) */
      this.el.querySelectorAll('[data-quiz-next]').forEach(btn => {
        btn.addEventListener('click', () => {
          const screenId = btn.closest('[data-quiz-screen]').dataset.quizScreen;
          const key = SCREEN_KEY_MAP[screenId];
          if (key) this.autoAdvance(key);
        });
      });
    },

    autoAdvance(key) {
      const next = ADVANCE_MAP[key];
      if (next === 'loading') {
        this.goTo('loading');
        this.computeResults();
      } else if (next) {
        this.goTo(next);
      }
    },

    goTo(screen) {
      /* Hide mobile proxy immediately during transition */
      if (this.proxyBtn) this.proxyBtn.classList.remove('emz-quiz__mobile-proxy--visible');

      /* Blur focused element first — prevents the browser from
         auto-scrolling to keep a focused button in view during
         the screen transition animation. */
      if (document.activeElement) document.activeElement.blur();

      const current = this.el.querySelector('.emz-quiz__screen--active');
      const next = this.el.querySelector(`[data-quiz-screen="${screen}"]`);
      if (!next || current === next) return;

      const prevScreen = this.currentScreen;
      const goingForward = SCREENS.indexOf(screen) > SCREENS.indexOf(prevScreen);

      /* Animate out */
      current.classList.add(goingForward ? 'emz-quiz__screen--exit-left' : 'emz-quiz__screen--exit-right');
      current.classList.remove('emz-quiz__screen--active');

      /* Animate in */
      next.classList.add(goingForward ? 'emz-quiz__screen--enter-right' : 'emz-quiz__screen--enter-left');
      next.classList.add('emz-quiz__screen--active');

      /* Clean up animation classes after transition */
      setTimeout(() => {
        current.classList.remove('emz-quiz__screen--exit-left', 'emz-quiz__screen--exit-right');
        next.classList.remove('emz-quiz__screen--enter-right', 'emz-quiz__screen--enter-left');
      }, 400);

      this.currentScreen = screen;
      this.updateProgress();
      this.updateBackButton();

      /* Scroll quiz into view — instant (not smooth) to prevent mobile
         browsers from interrupting the scroll mid-animation.  Fire twice:
         once immediately and once after animation completes (420ms) as a
         safety net in case the first scroll gets overridden by layout. */
      const scrollToQuiz = () => {
        this.el.scrollTop = 0;
        this.el.scrollIntoView({ behavior: 'auto', block: 'start' });
      };
      scrollToQuiz();
      setTimeout(scrollToQuiz, 420);
    },

    goBack() {
      const idx = SCREENS.indexOf(this.currentScreen);
      if (idx > 0) this.goTo(SCREENS[idx - 1]);
    },

    retake() {
      /* Hide mobile proxy */
      if (this.proxyBtn) this.proxyBtn.classList.remove('emz-quiz__mobile-proxy--visible');

      /* Reset answers */
      this.answers = { skinType: null, concerns: [], scent: [], category: [] };

      /* Reset all card selections */
      this.el.querySelectorAll('.emz-quiz__card--selected').forEach(c =>
        c.classList.remove('emz-quiz__card--selected'));

      /* Hide Continue buttons */
      this.el.querySelectorAll('.emz-quiz__next').forEach(b =>
        b.classList.remove('emz-quiz__next--visible'));

      /* Clear results */
      this.el.querySelector('[data-quiz-results-grid]').innerHTML = '';

      this.goTo('welcome');

      /* Scroll to top of page after the screen transition settles */
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 450);
    },

    updateProgress() {
      const progressMap = { welcome: 0, q1: 0.2, q2: 0.4, q3: 0.6, q4: 0.8, loading: 0.95, results: 1 };
      const bar = this.el.querySelector('[data-quiz-progress-bar]');
      const pct = progressMap[this.currentScreen] || 0;
      bar.style.width = (pct * 100) + '%';

      /* Hide progress on welcome and results */
      const progressEl = this.el.querySelector('[data-quiz-progress]');
      const hide = this.currentScreen === 'welcome' || this.currentScreen === 'results';
      progressEl.classList.toggle('emz-quiz__progress--hidden', hide);
    },

    updateBackButton() {
      const btn = this.el.querySelector('[data-quiz-back]');
      const show = ['q1', 'q2', 'q3', 'q4'].includes(this.currentScreen);
      btn.classList.toggle('emz-quiz__back--visible', show);
    },

    /* ===== MOBILE PROXY CONTINUE BUTTON =====
       On mobile, position:fixed inside the quiz's overflow:hidden
       container + CSS animation containing block = broken tap targets.
       Fix: create a real fixed button directly on document.body,
       completely outside the quiz DOM tree. */
    createMobileProxy() {
      const proxy = document.createElement('button');
      proxy.className = 'emz-quiz__mobile-proxy';
      proxy.innerHTML = 'Continue <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>';
      document.body.appendChild(proxy);
      this.proxyBtn = proxy;

      proxy.addEventListener('click', () => {
        const key = SCREEN_KEY_MAP[this.currentScreen];
        if (key) this.autoAdvance(key);
      });
    },

    /* Sync proxy visibility with the in-quiz Continue button state */
    syncProxy() {
      if (!this.proxyBtn) return;
      const screen = this.el.querySelector(`[data-quiz-screen="${this.currentScreen}"]`);
      const nextBtn = screen ? screen.querySelector('[data-quiz-next]') : null;
      const show = nextBtn && nextBtn.classList.contains('emz-quiz__next--visible');
      this.proxyBtn.classList.toggle('emz-quiz__mobile-proxy--visible', !!show);
    },

    /* ===== HIDE FLOATING WIDGETS (mobile) =====
       ShopifyChat (inbox-online-store-chat) is a custom element that
       ignores CSS display:none.  Force-hide via inline styles and
       watch for async load with MutationObserver. */
    hideFloatingWidgets() {
      const nuke = (sel) => {
        const el = document.querySelector(sel);
        if (el) {
          el.style.cssText += ';display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;height:0!important;width:0!important;overflow:hidden!important;position:fixed!important;top:-9999px!important;';
        }
      };
      const targets = ['#ShopifyChat', 'shopify-chat', 'inbox-online-store-chat', '#smile-ui-container'];
      targets.forEach(nuke);

      /* Chat widget often loads async — watch for it */
      const obs = new MutationObserver(() => { targets.forEach(nuke); });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => obs.disconnect(), 15000);
    },

    /* ===== MONEY FORMATTER ===== */
    formatMoney(cents) {
      var format = (window.theme && theme.settings && theme.settings.moneyFormat)
        || '${{amount}}';
      var amount = (cents / 100).toFixed(2);
      var amountNoDecimals = Math.floor(cents / 100).toString();
      var amountWithComma = amount.replace('.', ',');
      return format
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}',
          amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);
    },

    /* ===== CART ITEM HTML BUILDER =====
       Replicates the structure from snippets/cart-item.liquid so
       the drawer looks correct even if the theme's CartDrawer
       class doesn't re-render from the cart:updated event. */
    buildCartItemHtml(item) {
      var img = item.image
        ? item.image.replace(/(\.[a-z]+)(\?|$)/, '_180x$1$2')
        : '';
      var price = this.formatMoney(item.final_price);

      /* Strike-through original price if on sale */
      var salePriceHtml = '';
      if (item.original_price !== item.final_price) {
        salePriceHtml = '<small class="cart__price cart__price--strikethrough">'
          + this.formatMoney(item.original_price) + '</small>';
      }

      /* Variant info */
      var variantHtml = '';
      if (item.variant_title) {
        variantHtml = '<div class="cart__item--variants">';
        if (item.options_with_values && item.options_with_values.length) {
          for (var i = 0; i < item.options_with_values.length; i++) {
            var opt = item.options_with_values[i];
            variantHtml += '<div><span>' + opt.name + ':</span> ' + opt.value + '</div>';
          }
        } else {
          variantHtml += '<div>' + item.variant_title + '</div>';
        }
        variantHtml += '</div>';
      }

      return '<div class="cart__item" data-key="' + item.key + '">'
        + '<div class="cart__image">'
          + (img
            ? '<a href="' + item.url + '" class="image-wrap">'
              + '<img src="' + img + '" alt="' + item.product_title + '">'
            + '</a>'
            : '')
        + '</div>'
        + '<div class="cart__item-details">'
          + '<div class="cart__item-title">'
            + '<a href="' + item.url + '" class="cart__item-name">'
              + item.product_title
            + '</a>'
            + variantHtml
          + '</div>'
          + '<div class="cart__item-sub">'
            + '<div>'
              + '<div class="js-qty__wrapper">'
                + '<input type="text" class="js-qty__num"'
                  + ' value="' + item.quantity + '"'
                  + ' min="0" pattern="[0-9]*"'
                  + ' data-id="' + item.key + '" readonly>'
              + '</div>'
            + '</div>'
            + '<div class="cart__item-price-col text-right">'
              + salePriceHtml
              + '<span class="cart__price">' + price + '</span>'
            + '</div>'
          + '</div>'
        + '</div>'
      + '</div>';
    },

    /* ===== CART DRAWER REFRESH =====
       Uses the Shopify Section Rendering API to fetch fresh, server-
       rendered cart drawer HTML (bypasses CDN cache because sections
       are rendered per-request with current session cart state).
       Falls back to manual HTML build from /cart.js if section
       rendering doesn't return usable content.
       A MutationObserver guards against the theme wiping our injected
       content during its own re-render cycle. */
    async refreshCartDrawer() {
      try {
        /* 1. Fresh cart data (never cached) */
        var cart = await (await fetch('/cart.js')).json();
        if (!cart.items.length) return;

        /* 2. Section Rendering API — fresh server-rendered HTML */
        var freshProductsHtml = '';
        var freshSubtotalHtml = this.formatMoney(cart.total_price);

        try {
          var secRes = await fetch('/?sections=header');
          if (secRes.ok) {
            var secData = await secRes.json();
            var html = secData['header'] || '';
            if (html) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              var fp = tmp.querySelector('[data-products]');
              var fs = tmp.querySelector('[data-subtotal]');
              if (fp && fp.innerHTML.trim()) freshProductsHtml = fp.innerHTML;
              if (fs) freshSubtotalHtml = fs.innerHTML;
            }
          }
        } catch (e) { /* fall through to manual build */ }

        /* 3. Fallback: build HTML manually from /cart.js */
        if (!freshProductsHtml) {
          freshProductsHtml = cart.items.map(function(item) {
            return quiz.buildCartItemHtml(item);
          }).join('');
        }

        /* 4. Inject function — forces content + visibility */
        function inject() {
          var p = document.querySelector('#CartDrawer [data-products]');
          if (p) {
            p.innerHTML = freshProductsHtml;
            /* Override any appear-animation opacity rules */
            p.style.cssText += ';opacity:1 !important;visibility:visible !important;';
          }
          var s = document.querySelector('#CartDrawer [data-subtotal]');
          if (s) s.innerHTML = freshSubtotalHtml;

          /* Force .drawer__inner visible, .drawer__cart-empty hidden.
             Override display, opacity, visibility, height — covers all
             possible CSS hiding mechanisms the theme might use. */
          var inner = document.querySelector('#CartDrawer .drawer__inner');
          var empty = document.querySelector('#CartDrawer .drawer__cart-empty');
          if (inner) inner.style.cssText += ';display:block !important;opacity:1 !important;visibility:visible !important;height:auto !important;overflow:visible !important;';
          if (empty) empty.style.cssText += ';display:none !important;';

          /* Also force footer visible (for subtotal / checkout) */
          var footer = document.querySelector('#CartDrawer .drawer__footer');
          if (footer) footer.style.cssText += ';opacity:1 !important;visibility:visible !important;';
        }

        /* Safe inject — only if the container was emptied */
        function safeInject() {
          var p = document.querySelector('#CartDrawer [data-products]');
          if (p && p.children.length === 0) inject();
        }

        /* 5. MutationObserver — re-inject if the theme wipes content */
        var pe = document.querySelector('#CartDrawer [data-products]');
        if (pe) {
          var obs = new MutationObserver(function() {
            var el = document.querySelector('#CartDrawer [data-products]');
            if (el && el.children.length === 0) inject();
          });
          obs.observe(pe, { childList: true });
          setTimeout(function() { obs.disconnect(); }, 5000);
        }

        /* 6. First injection (unconditional — our data is fresh) */
        inject();

        /* 7. Update header cart count */
        document.querySelectorAll('.cart-link__bubble-num, .cart-link__count, [data-cart-count]').forEach(function(el) {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-link__bubble').forEach(function(el) {
          el.classList.toggle('cart-link__bubble--visible', cart.item_count > 0);
        });

        /* 8. Open the drawer — click cart icon for native behavior */
        var cartIcon = document.querySelector('.js-drawer-open-cart');
        if (cartIcon) {
          cartIcon.click();
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }

        /* 9. Delayed re-injections — beat any async theme re-renders */
        setTimeout(safeInject, 200);
        setTimeout(safeInject, 600);
        setTimeout(safeInject, 1200);

        console.log('[EmzQuiz] Cart refresh:', {
          sectionAPI: !!freshProductsHtml,
          items: cart.items.length,
          drawerFound: !!document.querySelector('#CartDrawer'),
          productsContainer: !!pe
        });
      } catch (e) {
        console.warn('[EmzQuiz] Cart refresh error:', e);
      }
    },

    /* ===== MATCH REASON BUILDER ===== */
    buildMatchReason(product, answers, suppressScent) {
      var parts = [];

      /* Skin type */
      if (answers.skinType && product.skinTypes.indexOf(answers.skinType) !== -1) {
        var label = answers.skinType.charAt(0).toUpperCase() + answers.skinType.slice(1);
        parts.push('Great for ' + label.toLowerCase() + ' skin');
      }

      /* Concerns */
      var concernLabels = {
        eczema: 'eczema', dryness: 'dryness', aging: 'aging',
        breakouts: 'breakouts', redness: 'redness', healing: 'healing & recovery'
      };
      var matchedConcerns = [];
      if (answers.concerns && answers.concerns.length) {
        for (var i = 0; i < answers.concerns.length; i++) {
          var c = answers.concerns[i];
          if (c !== 'none' && product.concerns.indexOf(c) !== -1) {
            matchedConcerns.push(concernLabels[c] || c);
          }
        }
      }
      if (matchedConcerns.length === 1) {
        parts.push('Helps with ' + matchedConcerns[0]);
      } else if (matchedConcerns.length > 1) {
        parts.push('Helps with ' + matchedConcerns.slice(0, -1).join(', ') + ' & ' + matchedConcerns[matchedConcerns.length - 1]);
      }

      /* Scent — skip when user selected "Surprise Me" (any) */
      if (!suppressScent && answers.scent && answers.scent.length && !answers.scent.includes('any')) {
        var scentLabels = {
          floral: 'Floral', herbal: 'Herbal', citrus: 'Citrus',
          fresh: 'Fresh', fruity: 'Fruity', woodsy: 'Woodsy',
          earthy: 'Earthy', sweet: 'Sweet', warm: 'Warm',
          light: 'Lightly scented', unscented: 'Unscented'
        };
        var matchedScents = [];
        for (var j = 0; j < answers.scent.length; j++) {
          if (product.scents.indexOf(answers.scent[j]) !== -1) {
            matchedScents.push(scentLabels[answers.scent[j]] || answers.scent[j]);
          }
        }
        if (matchedScents.length) {
          var scentStr = matchedScents.slice(0, 2).join(' & ');
          parts.push(scentStr + ' scent');
        }
      }

      return parts.length ? parts.join(' \u00B7 ') : product.reason;
    },

    /* ===== SCORING ENGINE ===== */
    computeResults() {
      const config = window.EmzQuizConfig;
      if (!config || !config.products) {
        console.error('EmzQuizConfig not loaded');
        this.goTo('results');
        return;
      }

      const a = this.answers;

      /* Determine if scent should be weighted.
         When the user picks "face", "mens", "baby", or "lip" as a
         category, scent is far less relevant, so we zero it out. */
      const cats = a.category.length ? a.category : ['soap'];
      const scentCategories = ['face', 'mens', 'baby', 'lip'];
      const suppressScent = cats.some(c => scentCategories.includes(c)) && cats.length === 1;
      /* Only suppress if face/mens/baby/lip is the ONLY category selected.
         If they pick face + soap, scent still matters for soap results. */

      /* "Surprise Me" (any) — don't penalize or reward scent */
      const scentIsAny = a.scent.includes('any');

      const scored = config.products.map(product => {
        let score = product.boost || 0;

        /* Skin type match (high weight) */
        if (a.skinType && product.skinTypes.includes(a.skinType)) {
          score += 5;
        }

        /* Category match (high weight, required) */
        let catMatch = false;
        cats.forEach(c => {
          if (product.categories.includes(c)) { score += 5; catMatch = true; }
        });

        /* Penalize if no category match at all */
        if (!catMatch) score -= 8;

        /* Concerns match (medium weight, additive) */
        const concerns = a.concerns.length ? a.concerns : ['none'];
        concerns.forEach(c => {
          if (product.concerns.includes(c)) score += 3;
        });

        /* Scent match / mismatch (weighted or zeroed depending on category).
           Multi-select: +4 if ANY of the user's scent picks overlap with
           the product's scent tags.  -3 if zero overlap.  This rewards
           products matching any preferred scent and penalizes products
           that match none of them.
           When "Surprise Me" (any) is selected, skip scent scoring
           entirely — all scents are equally welcome. */
        if (!suppressScent && !scentIsAny && a.scent.length) {
          const hasOverlap = a.scent.some(s => product.scents.includes(s));
          if (hasOverlap) {
            score += 4;
          } else {
            score -= 3;
          }
        }

        /* Build dynamic match reason */
        const matchReason = this.buildMatchReason(product, a, suppressScent);

        return { ...product, score, matchReason };
      });

      /* Sort by score descending */
      scored.sort((a, b) => b.score - a.score);

      /* ---- Smart distribution (6 results) ----
         Guarantee at least one top-scorer from each selected
         category so the results feel representative, then fill
         remaining slots from overall highest scores. */
      const positive = scored.filter(p => p.score > 0);
      const MAX = 6;
      const picked = [];
      const pickedHandles = new Set();

      /* First pass: one top scorer per selected category */
      for (let ci = 0; ci < cats.length; ci++) {
        if (picked.length >= MAX) break;
        for (let pi = 0; pi < positive.length; pi++) {
          var p = positive[pi];
          if (!pickedHandles.has(p.handle) && p.categories.includes(cats[ci])) {
            picked.push(p);
            pickedHandles.add(p.handle);
            break;
          }
        }
      }

      /* Fill remaining slots from overall top scores */
      for (let fi = 0; fi < positive.length; fi++) {
        if (picked.length >= MAX) break;
        if (!pickedHandles.has(positive[fi].handle)) {
          picked.push(positive[fi]);
          pickedHandles.add(positive[fi].handle);
        }
      }

      /* Ensure minimum 2 results */
      if (picked.length < 2) {
        for (let ri = 0; ri < scored.length; ri++) {
          if (picked.length >= 2) break;
          if (!pickedHandles.has(scored[ri].handle)) {
            picked.push(scored[ri]);
            pickedHandles.add(scored[ri].handle);
          }
        }
      }

      /* Fetch product data from Shopify and render */
      this.fetchAndRender(picked);
    },

    async fetchAndRender(recommendations) {
      const products = [];

      for (const rec of recommendations) {
        try {
          const res = await fetch(`/products/${rec.handle}.js`);
          if (!res.ok) continue;
          const data = await res.json();
          products.push({ shopify: data, matchReason: rec.matchReason });
        } catch (e) {
          console.warn(`Could not fetch product: ${rec.handle}`, e);
        }
      }

      this.renderResults(products);

      /* Small delay for the loading animation feel */
      setTimeout(() => this.goTo('results'), 800);
    },

    renderResults(products) {
      const grid = this.el.querySelector('[data-quiz-results-grid]');
      const summary = this.el.querySelector('[data-quiz-results-summary]');

      /* Build summary text */
      const skinLabel = this.answers.skinType
        ? this.answers.skinType.charAt(0).toUpperCase() + this.answers.skinType.slice(1)
        : '';
      summary.textContent = `Based on your ${skinLabel.toLowerCase()} skin profile, here's what we'd put in your hands.`;

      /* Build product cards */
      grid.innerHTML = products.map(({ shopify, matchReason }) => {
        const img = shopify.featured_image
          ? shopify.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_400x$1$2')
          : '';
        const price = (shopify.price / 100).toFixed(2);
        const comparePrice = shopify.compare_at_price
          ? (shopify.compare_at_price / 100).toFixed(2)
          : null;
        const variantId = shopify.variants[0].id;
        const url = `/products/${shopify.handle}`;

        return `
          <div class="emz-quiz__result-card">
            <a href="${url}" class="emz-quiz__result-image-link">
              <img src="${img}" alt="${shopify.title}" class="emz-quiz__result-image" loading="lazy">
            </a>
            <div class="emz-quiz__result-body">
              <a href="${url}" class="emz-quiz__result-title">${shopify.title}</a>
              <div class="emz-quiz__result-price">
                ${comparePrice ? `<span class="emz-quiz__result-compare">$${comparePrice}</span>` : ''}
                <span>$${price}</span>
              </div>
              <p class="emz-quiz__result-reason"><span class="emz-quiz__match-icon">\u2726</span> ${matchReason}</p>
              <button class="emz-quiz__add-to-cart" data-variant-id="${variantId}">
                Add to Cart
              </button>
            </div>
          </div>
        `;
      }).join('');

      /* Bind add-to-cart buttons */
      grid.querySelectorAll('.emz-quiz__add-to-cart').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const vid = btn.dataset.variantId;
          btn.textContent = 'Adding...';
          btn.disabled = true;

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: parseInt(vid), quantity: 1 }] })
            });

            if (res.ok) {
              btn.textContent = 'Added \u2713';
              btn.classList.add('emz-quiz__add-to-cart--added');

              /* Open drawer + inject fresh cart content.
                 Uses Section Rendering API for server-rendered HTML,
                 with MutationObserver defense against theme re-renders. */
              await quiz.refreshCartDrawer();

              setTimeout(() => {
                btn.textContent = 'Add to Cart';
                btn.disabled = false;
                btn.classList.remove('emz-quiz__add-to-cart--added');
              }, 2500);
            } else {
              btn.textContent = 'Try Again';
              btn.disabled = false;
            }
          } catch (err) {
            btn.textContent = 'Try Again';
            btn.disabled = false;
          }
        });
      });
    }
  };

  /* Boot */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => quiz.init());
  } else {
    quiz.init();
  }
})();
